import { useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Star, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import {
  breadcrumbSchema,
  faqPageSchema,
  legalServiceSchema,
  attorneySchema,
} from "@/lib/schemas";
import {
  findTrustService,
  TRUSTS_PHONE,
  TRUSTS_TOLL_FREE,
} from "@/lib/trusts";
import { trackAuthorityPageView } from "@/lib/analytics";

/**
 * TrustService — /trusts/:slug
 *
 * Reusable page shell for the eight trust service sub-pages. Content comes
 * entirely from src/lib/trusts.js. Each page renders the seven modules
 * required by the Trusts Authority Hub spec:
 *
 *   1. Direct answer     (the "so what" up top)
 *   2. Best fit          (who it serves)
 *   3. Key planning issues / representative modules
 *   4. Process           (shared, referenced back to the pillar)
 *   5. FAQs              (page-specific)
 *   6. Related services  (internal-link discipline)
 *   7. Confidential-conference CTA
 */
export default function TrustService() {
  const { slug } = useParams();
  const svc = findTrustService(slug);
  useEffect(() => {
    if (!svc || !svc.priorityFaqs) return;
    trackAuthorityPageView({
      page_slug: `trusts/${svc.slug}`,
      page_title: svc.title,
      priority_faq_count: svc.priorityFaqs.length,
    });
  }, [svc]);
  if (!svc) return <Navigate to="/trusts" replace />;

  const path = `/trusts/${svc.slug}`;
  // Merge priority FAQs (if present) with page-specific FAQs for FAQPage schema.
  const schemaFaqs = [...(svc.priorityFaqs || []), ...svc.faqs];
  const schemas = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Trusts", path: "/trusts" },
      { name: svc.menu, path },
    ]),
    legalServiceSchema({
      name: svc.menu,
      description: svc.metaDesc,
      url: `https://lonestarcounsel.com${path}`,
    }),
    attorneySchema(),
    faqPageSchema(schemaFaqs),
  ];

  return (
    <div className="bg-ivory" data-testid={`trust-service-${svc.slug}`}>
      <SEO
        title={svc.title}
        description={svc.metaDesc}
        path={path}
        schema={schemas}
      />

      {/* HERO */}
      <section className="relative panel-navy grain grain-dark py-24 md:py-32" data-testid="ts-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium">
            <Link to="/trusts" className="hover:text-ivory transition-colors" data-testid="ts-breadcrumb-trusts">
              Trusts &amp; Estate Planning
            </Link>
            <span className="text-ivory/40 mx-3">/</span>
            <span className="text-ivory/70">{svc.menu}</span>
          </div>
          <h1 className="mt-8 font-display text-ivory text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-4xl" data-testid="ts-h1">
            {svc.h1}.
          </h1>
          <p className="mt-8 max-w-3xl text-lg md:text-xl text-ivory/85 font-light leading-relaxed" data-testid="ts-direct-answer">
            {svc.directAnswer}
          </p>
          {svc.attorneyReviewed && (
            <div className="mt-6 max-w-3xl">
              <AttorneyReviewNote reviewed={svc.attorneyReviewed} variant="dark" compact />
            </div>
          )}
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/intake" className="btn-brass inline-flex items-center gap-2" data-testid="ts-hero-cta">
              <Star size={11} className="fill-current" />
              Request a Confidential Conference
            </Link>
            <a
              href={`tel:${TRUSTS_PHONE.replace(/\D/g, "")}`}
              className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/50 hover:border-brass pb-1"
              data-testid="ts-hero-phone"
            >
              <Phone size={11} className="inline mr-2" />
              Call {TRUSTS_PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* WHO IT SERVES + KEY MODULES */}
      <section className="bg-ivory py-24 md:py-32" data-testid="ts-modules">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker">Best fit</div>
              <p className="mt-6 font-cormorant italic text-2xl md:text-3xl text-navy/85 leading-snug" data-testid="ts-best-fit">
                {svc.bestFit}
              </p>
              {svc.notes && (
                <p className="mt-8 text-sm text-oxblood leading-relaxed border-l-2 border-oxblood/40 pl-4" data-testid="ts-notes">
                  {svc.notes}
                </p>
              )}
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <div className="kicker">Representative planning issues</div>
              <ul className="mt-8 space-y-4">
                {svc.modules.map((m, i) => (
                  <li key={i} className="flex gap-3 hairline-top pt-4" data-testid={`ts-module-${i}`}>
                    <Star size={10} className="text-brass fill-brass mt-2 shrink-0" />
                    <span className="text-navy/85 leading-relaxed">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHY COORDINATED (optional, authority-hub extension) */}
      {svc.whyCoordinated && (
        <section className="bg-linen hairline-top py-20 md:py-28" data-testid="ts-why-coordinated">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <div className="kicker">Direct answer</div>
            <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
              {svc.whyCoordinated.heading[0]}{" "}
              <span className="italic font-cormorant text-oxblood">{svc.whyCoordinated.heading[1]}</span>
            </h2>
            <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
              {svc.whyCoordinated.lede}
            </p>
            <ul className="mt-10 space-y-4 max-w-4xl" data-testid="ts-why-coordinated-list">
              {svc.whyCoordinated.points.map((p, i) => (
                <li key={i} className="flex gap-3 hairline-top pt-4" data-testid={`ts-why-${i}`}>
                  <Star size={10} className="text-brass fill-brass mt-2 shrink-0" />
                  <span className="text-navy/85 leading-relaxed">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* COORDINATION STEPS (optional, authority-hub extension) */}
      {svc.coordinationSteps && (
        <section className="bg-ivory py-20 md:py-28" data-testid="ts-coordination-steps">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="kicker">Scope of the engagement</div>
            <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
              {svc.coordinationSteps.heading[0]}{" "}
              <span className="italic font-cormorant text-oxblood">{svc.coordinationSteps.heading[1]}</span>
            </h2>
            <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
              {svc.coordinationSteps.lede}
            </p>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="ts-coordination-list">
              {svc.coordinationSteps.items.map(([title, desc], i) => (
                <li key={i} className="py-4 hairline-top" data-testid={`ts-coordination-${i}`}>
                  <div className="flex items-baseline gap-3">
                    <Star size={9} className="text-brass fill-brass" aria-hidden />
                    <h3 className="font-display text-lg text-navy">{title}</h3>
                  </div>
                  <p className="mt-2 pl-6 text-sm text-navy/75 leading-relaxed font-serif">{desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* PROCESS (shared cadence) */}
      <section className="bg-linen hairline-top py-20 md:py-28" data-testid="ts-process">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">How we work</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-4xl leading-[1.05]">
            The Lone Star Counsel process,{" "}
            <span className="italic font-cormorant text-oxblood">adapted to the matter.</span>
          </h2>
          <ol className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              ["Understand", "Family, assets, and objectives."],
              ["Design", "Legal architecture appropriate to the matter."],
              ["Draft", "Trust and companion instruments."],
              ["Fund", "Assets and beneficiary designations aligned."],
              ["Maintain", "Reviews as life and law evolve."],
            ].map(([h, b], i) => (
              <li key={i} className="hairline-top pt-5" data-testid={`ts-process-step-${i}`}>
                <div className="font-display text-2xl text-brass">0{i + 1}</div>
                <div className="mt-3 font-display text-navy text-base uppercase tracking-wider">{h}</div>
                <p className="mt-2 text-sm text-navy/75 leading-relaxed">{b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQS */}
      <section className="bg-ivory py-24 md:py-32" data-testid="ts-faqs">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently asked</div>
          <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
            About {svc.menu.toLowerCase()}.
          </h2>
          {svc.priorityFaqs && (
            <dl className="mt-12 space-y-8" data-testid="ts-priority-faqs">
              {svc.priorityFaqs.map(({ q, a }, i) => (
                <div key={`priority-${i}`} className="hairline-top pt-6" data-testid={`ts-priority-faq-${i}`}>
                  <dt className="font-display text-xl md:text-2xl text-navy leading-snug">{q}</dt>
                  <dd className="mt-3 text-navy/80 leading-relaxed">{a}</dd>
                </div>
              ))}
            </dl>
          )}
          <dl className={`${svc.priorityFaqs ? "mt-16 pt-10 border-t border-brass/30" : "mt-12"} space-y-8`}>
            {svc.priorityFaqs && (
              <div className="kicker !mb-2" data-testid="ts-extended-faq-kicker">Additional questions</div>
            )}
            {svc.faqs.map(({ q, a }, i) => (
              <div key={i} className="hairline-top pt-6" data-testid={`ts-faq-${i}`}>
                <dt className="font-display text-xl md:text-2xl text-navy leading-snug">{q}</dt>
                <dd className="mt-3 text-navy/80 leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CONTEXTUAL LINKS (optional, authority-hub extension) */}
      {svc.contextualLinks && (
        <section className="bg-linen hairline-top py-20 md:py-28" data-testid="ts-contextual-links">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="kicker">Related counsel</div>
            <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
              Coordinate the plan{" "}
              <span className="italic font-cormorant text-oxblood">across the disciplines.</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-testid="ts-contextual-list">
              {svc.contextualLinks.map((c, i) => (
                <Link
                  key={i}
                  to={c.href}
                  className="group hairline-top pt-5 block hover:bg-ivory/60 transition-colors -mx-3 px-3"
                  data-testid={`ts-contextual-${i}`}
                >
                  <div className="flex items-baseline gap-3">
                    <Star size={9} className="text-brass fill-brass" aria-hidden />
                    <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                      {c.label}
                    </span>
                  </div>
                  <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                    {c.desc}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED */}
      <section className="bg-linen hairline-top py-20 md:py-24" data-testid="ts-related">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Related trust services</div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            {svc.related.map((r, i) => (
              <Link
                key={r.slug}
                to={`/trusts/${r.slug}`}
                className="group hairline-top pt-5 block hover:bg-ivory/60 transition-colors -mx-3 px-3"
                data-testid={`ts-related-${i}`}
              >
                <div className="flex items-baseline gap-3">
                  <Star size={9} className="text-brass fill-brass" />
                  <span className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                    Continue reading
                  </span>
                </div>
                <h3 className="mt-3 font-display text-xl md:text-2xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                  {r.label}
                </h3>
              </Link>
            ))}
          </div>
          <div className="mt-14 hairline-top pt-8 flex flex-wrap items-baseline gap-6">
            <Link to="/trusts" className="text-[11px] uppercase tracking-[0.32em] text-navy hover:text-oxblood transition-colors font-medium border-b border-brass/40 pb-0.5" data-testid="ts-related-hub">
              &larr; All Trust Planning Services
            </Link>
            <Link to="/contact" className="text-[11px] uppercase tracking-[0.32em] text-navy hover:text-oxblood transition-colors font-medium border-b border-brass/40 pb-0.5" data-testid="ts-related-contact">
              Contact the firm
            </Link>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="panel-navy grain grain-dark py-20 md:py-28 relative overflow-hidden" data-testid="ts-cta">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <Star size={11} className="text-brass fill-brass mx-auto" />
          {svc.customCta ? (
            <>
              <div className="mt-8 text-[10px] uppercase tracking-[0.36em] text-brass font-medium" data-testid="ts-custom-cta-kicker">
                {svc.customCta.kicker}
              </div>
              <h2 className="mt-4 font-display text-ivory text-3xl md:text-5xl leading-[1.05] max-w-3xl mx-auto" data-testid="ts-custom-cta-heading">
                {svc.customCta.heading[0]}{" "}
                <span className="italic font-cormorant text-brass">{svc.customCta.heading[1]}</span>
              </h2>
              <p className="mt-8 max-w-2xl mx-auto text-ivory/85 font-serif text-lg leading-relaxed" data-testid="ts-custom-cta-lede">
                {svc.customCta.lede}
              </p>
              <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                <Link to={svc.customCta.primaryHref} className="btn-brass" data-testid="ts-custom-cta-intake">
                  {svc.customCta.primaryLabel}
                </Link>
                <a
                  href={`tel:${TRUSTS_PHONE.replace(/\D/g, "")}`}
                  className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/40 hover:border-brass pb-1"
                  data-testid="ts-custom-cta-phone"
                >
                  or call {TRUSTS_PHONE} · Toll-Free {TRUSTS_TOLL_FREE}
                </a>
              </div>
            </>
          ) : (
            <>
              <h2 className="mt-8 font-display text-ivory text-3xl md:text-5xl leading-[1.05] max-w-3xl mx-auto">
                A confidential conference with{" "}
                <span className="italic font-cormorant text-brass">Darryl V. Pratt, Esq., CPA.</span>
              </h2>
              <p className="mt-8 max-w-2xl mx-auto font-cormorant italic text-lg md:text-xl text-ivory/80 leading-snug">
                Nearly 30 years of business, estate, trust, and succession counsel. Frisco headquarters, or by secure video across the state of Texas.
              </p>
              <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                <Link to="/intake" className="btn-brass" data-testid="ts-cta-intake">
                  Request a Confidential Conference
                </Link>
                <a
                  href={`tel:${TRUSTS_PHONE.replace(/\D/g, "")}`}
                  className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/40 hover:border-brass pb-1"
                  data-testid="ts-cta-phone"
                >
                  or call {TRUSTS_PHONE} · Toll-Free {TRUSTS_TOLL_FREE}
                </a>
              </div>
            </>
          )}
          <div className="mt-14 max-w-3xl mx-auto text-[10px] uppercase tracking-[0.28em] text-ivory/50 leading-relaxed">
            Attorney Advertising. Pratt Law Group, PLLC d/b/a LONE STAR ★ COUNSEL. This page is provided for general educational purposes and does not constitute legal or tax advice. No attorney-client relationship is formed by using this site or contacting the firm. Selection and implementation of a trust depend on the client's family, assets, tax, fiduciary, and succession circumstances. Prior results do not guarantee a similar outcome.
          </div>
          <div className="max-w-3xl mx-auto">
            <AttorneyReviewNote reviewed={svc.attorneyReviewed || "February 2026"} variant="dark" compact />
          </div>
        </div>
      </section>
    </div>
  );
}
